import { useEffect, useRef, useState } from 'react'

import { Skeleton } from '@mui/material'
import useDebounce from 'commons/customHooks/useDebounce'
import { formatDate } from 'commons/utils/date'
import { formatToPTBRNumber, numberToDecimalString } from 'commons/utils/number'
import {
  DSInputType,
  type HeadItemType,
  Input,
  NotFound,
  Pagination,
  Select,
  Table,
  Typography,
} from 'components'
import { useParams } from 'react-router-dom'
import type { Faturamento } from 'services/manage_ov/interface'
import { asyncGetInvoicesListByOvNumber } from 'store/OVContext/ovSlice'
import { useDispatch, useSelector } from 'store/store'

import InvoiceDetailModal from '../InvoiceDetailModal'
import * as S from './styles'

const InvoicesTable = () => {
  const { ovNumber } = useParams()
  const pageSize = 10

  const [searchValue, setSearchValue] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)
  const searchRef = useRef<HTMLInputElement>(null)

  const [invoices, setInvoices] = useState<Faturamento[]>([])

  const [selectedInvoice, setSelectedInvoice] = useState<string>('')
  const [openInvoiceDetail, setOpenInvoiceDetail] = useState(false)
  const [viewOptions, setViewOptions] = useState<'doc' | 'sku'>('doc')

  const dispatch = useDispatch()
  const { invoicesInfo, invoiceIsLoading: loading } = useSelector((state) => state.ov)
  const tableViewOptions = [
    { title: 'Documento', value: 'doc' },
    { title: 'SKU', value: 'sku' },
  ]

  const headValues: HeadItemType[] = [
    { columnName: `DocumentoFaturamento`, label: 'Doc. Faturamento' },
    { columnName: 'NotaFiscal', label: 'Nota Fiscal' },
    { columnName: 'DataFaturamento', label: 'Data Faturamento' },
    { columnName: 'VlrFaturado', label: 'Vlr. Faturado' },
    { columnName: 'Estorno', label: 'Estorno' },
    { columnName: 'DataEstorno', label: 'Data Estorno' },
    { columnName: 'Transportadora1Etapa', label: 'Transportadora 1ª Etapa' },
    { columnName: 'DtPrevEntrega1Etp', label: 'Dt. Prev. Entrg. 1ª Etp.' },
    { columnName: 'DtRealEntrega1Etp', label: 'Dt. Real Entrg. 1ª Etp.' },
    { columnName: 'Transportadora2Etapa', label: 'Transportadora 2ª etapa' },
    { columnName: 'DtPrevEntrega2Etp', label: 'Dt. Prev. Entrg. 2ª Etp.' },
    { columnName: 'DtRealEntrega2Etp', label: 'Dt. Real Entrg. 2ª Etp.' },
  ]

  const headSkuValues: HeadItemType[] = [
    { columnName: 'Material', label: 'Material' },
    { columnName: 'Cor', label: 'Cor' },
    { columnName: 'Tamanho', label: 'Tamanho' },
    { columnName: 'Quantidade', label: 'Quantidade' },
    { columnName: 'DocumentoFaturamento', label: 'Doc. Faturamento' },
    { columnName: 'NotaFiscal', label: 'Nota Fiscal' },
    { columnName: 'DataFaturamento', label: 'Data Faturamento' },
    { columnName: 'VlrFaturado', label: 'Vlr. Faturado' },
    { columnName: 'Estorno', label: 'Estorno' },
    { columnName: 'DataEstorno', label: 'Data Estorno' },
    { columnName: 'Transportadora1Etapa', label: 'Transportadora 1ª Etapa' },
    { columnName: 'DtPrevEntrega1Etp', label: 'Dt. Prev. Entrg. 1ª Etp.' },
    { columnName: 'DtRealEntrega1Etp', label: 'Dt. Real Entrg. 1ª Etp.' },
    { columnName: 'Transportadora2Etapa', label: 'Transportadora 2ª etapa' },
    { columnName: 'DtPrevEntrega2Etp', label: 'Dt. Prev. Entrg. 2ª Etp.' },
    { columnName: 'DtRealEntrega2Etp', label: 'Dt. Real Entrg. 2ª Etp.' },
  ]

  const getInvoices = (page?: number, search?: string) => {
    const pageQuery = page || currentPage

    dispatch(
      asyncGetInvoicesListByOvNumber(viewOptions, `${ovNumber}`, {
        currentPage: pageQuery - 1,
        pageSize,
        searchValue: search || '',
      }),
    )
    setCurrentPage(pageQuery)
  }
  const debounceChange = useDebounce(getInvoices, 1000)
  useEffect(() => {
    if (invoicesInfo?.Faturamentos?.length > 0) {
      setInvoices(invoicesInfo.Faturamentos)
      const qtdOfRows = invoicesInfo.RowsCount
      const amountOfPages = Math.ceil(qtdOfRows / pageSize)
      setTotalPages(amountOfPages)
      setCurrentPage((currentPg) => (currentPg > amountOfPages ? amountOfPages : currentPg))
    } else {
      setTotalPages(0)
      setCurrentPage(1)
      setInvoices([])
    }
  }, [invoicesInfo])

  useEffect(() => {
    if (!loading) {
      searchRef.current?.focus()
    }
  }, [loading])

  useEffect(() => {
    getInvoices(1, '')
  }, [ovNumber, viewOptions])

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toLocaleUpperCase()
    setSearchValue(value)
    debounceChange(1, value)
  }

  const handleInvoiceSelect = (id: string) => {
    setSelectedInvoice(id)
    setOpenInvoiceDetail(true)
  }

  const renderSkeleton = () => {
    const skeleton = []
    for (let i = 0; i < 3; i++) {
      skeleton.push({
        content: Array(viewOptions === 'doc' ? headValues.length : headSkuValues.length)
          .fill('')
          .map((_, index) => <Skeleton height={20} key={index} width={100} />),
        id: `invoice-loading-${i}`,
      })
    }
    return skeleton
  }

  const renderSummary = () => {
    return (
      <S.SummaryWrapper>
        <Pagination
          currentPage={currentPage}
          itemsPerPage={pageSize}
          numberOfItems={invoicesInfo.RowsCount}
          numberOfPages={totalPages}
          setCurrentPage={(value: number) => getInvoices(value, searchValue)}
        />
        <S.SummaryRow>
          <S.SummaryItem>
            <Typography design="hav-default-02-text">Qtd. total</Typography>
            <span>{numberToDecimalString(invoicesInfo?.TotalQuantidade ?? 0)}</span>
          </S.SummaryItem>
          <S.SummaryItem>
            <Typography design="hav-default-02-text">Valor total</Typography>
            <span>{formatToPTBRNumber(invoicesInfo?.TotalValor ?? 0)}</span>
          </S.SummaryItem>
        </S.SummaryRow>
      </S.SummaryWrapper>
    )
  }

  const renderBody = () => {
    const content = loading
      ? renderSkeleton()
      : invoices.map((invoice, index) => {
          return {
            content: [
              invoice.NumeroDocumentoFatura,
              `${invoice.NumerNotaFiscal}/${invoice.NumeroSerie}`,
              formatDate(invoice.DataFaturamento),
              formatToPTBRNumber(invoice.ValorTotal),
              invoice.FlagEstorno ? 'Sim' : 'Não',
              invoice.DataEstorno ? formatDate(invoice.DataEstorno) : '',
              invoice.TrasportadoraEtapaUm,
              invoice.DataPrevisaoEntregaUm ? formatDate(invoice.DataPrevisaoEntregaUm) : '',
              invoice.DataEntregaRealizadaUm ? formatDate(invoice.DataEntregaRealizadaUm) : '',
              invoice.RazaoSocialTransportadoraDois,
              invoice.DataPrevisaoEntregaDois ? formatDate(invoice.DataPrevisaoEntregaDois) : '',
              invoice.DataEntregaRealizadaDois ? formatDate(invoice.DataEntregaRealizadaDois) : '',
            ],
            id: `invoice-${index}`,
            onClick: () => handleInvoiceSelect(invoice.NumeroDocumentoFatura),
          }
        })
    return content
  }
  const renderSkuBody = () => {
    const content = loading
      ? renderSkeleton()
      : invoices.map((invoice, index) => {
          return {
            content: [
              invoice?.CodigoMaterial,
              invoice?.CodigoCor,
              invoice?.CodigoTamnho,
              invoice?.Quantidade,
              invoice.NumeroDocumentoFatura,
              invoice.NumerNotaFiscal,
              formatDate(invoice.DataFaturamento),
              formatToPTBRNumber(invoice.Valor ?? 0),
              invoice.FlagEstorno ? 'Sim' : 'Não',
              invoice.DataEstorno ? formatDate(invoice.DataEstorno) : '',
              invoice.TrasportadoraEtapaUm,
              invoice.DataPrevisaoEntregaUm ? formatDate(invoice.DataPrevisaoEntregaUm) : '',
              invoice.DataEntregaRealizadaUm ? formatDate(invoice.DataEntregaRealizadaUm) : '',
              invoice.RazaoSocialTransportadoraDois,
              invoice.DataPrevisaoEntregaDois ? formatDate(invoice.DataPrevisaoEntregaDois) : '',
              invoice.DataEntregaRealizadaDois ? formatDate(invoice.DataEntregaRealizadaDois) : '',
            ],
            id: `invoice-${index}`,
          }
        })

    return content
  }

  return (
    <>
      <S.Header>
        <S.HeaderField width={viewOptions === 'doc' ? '132px' : '83px'}>
          <Select
            isMenu
            name="TableView"
            onChange={(value) => {
              setViewOptions(value.target.value as unknown as 'doc' | 'sku')
              setSearchValue('')
            }}
            options={tableViewOptions}
            value={viewOptions}
          />
        </S.HeaderField>
        <S.HeaderField width="300px">
          <Input
            addorment
            design={DSInputType.SEARCH_INPUT}
            disabled={loading}
            InputProps={{ placeholder: 'Buscar material em faturamento' }}
            onChange={handleSearch}
            ref={searchRef}
            value={searchValue}
          />
        </S.HeaderField>
      </S.Header>
      {!loading && !invoices.length ? (
        <NotFound
          description="As informações de faturamento da Ordem de Venda serão listadas aqui."
          title="Nenhuma fatura encontrada"
        />
      ) : (
        <>
          {viewOptions === 'doc' ? (
            <>
              <S.TableWrapper>
                <Table
                  bodyData={renderBody()}
                  distributeWidth={false}
                  fixedFirstCol
                  headData={headValues}
                  rowHeight={32}
                />
              </S.TableWrapper>
              {renderSummary()}
            </>
          ) : (
            <>
              <S.TableWrapper>
                <Table
                  bodyData={renderSkuBody()}
                  distributeWidth={false}
                  fixedFirstCol
                  headData={headSkuValues}
                  rowHeight={32}
                />
              </S.TableWrapper>
              {renderSummary()}
            </>
          )}
        </>
      )}
      {openInvoiceDetail && (
        <InvoiceDetailModal
          id={selectedInvoice}
          open={openInvoiceDetail}
          setOpen={setOpenInvoiceDetail}
        />
      )}
    </>
  )
}

export default InvoicesTable
